import { useEffect, useState } from 'react';
import {
  getAvatarColorForSeed,
  getInitialsFromDisplayName,
} from '../../utils/userAvatar';

/**
 * Circular avatar: optional profile photo with fallback to initials + stable color.
 *
 * @param {object} props
 * @param {string} [props.name] - Display name for initials + color seed
 * @param {string} [props.pictureUrl] - If set (and not initialsOnly), try photo first; on error → initials
 * @param {boolean} [props.initialsOnly] - Never load a photo (e.g. notification list)
 * @param {string} [props.className] - Extra classes on outer span
 * @param {string} [props.sizeClass] - Tailwind size (default h-10 w-10)
 * @param {string} [props.textClass] - Tailwind text size
 * @param {string} [props.ariaLabel] - If set, exposed as aria-label (e.g. user name)
 */
export default function UserInitialsAvatar({
  name,
  pictureUrl,
  initialsOnly = false,
  className = '',
  sizeClass = 'h-10 w-10',
  textClass = 'text-xs',
  ariaLabel,
}) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [pictureUrl]);

  const initials = getInitialsFromDisplayName(name);
  const bg = getAvatarColorForSeed(name || initials);
  const url = pictureUrl != null ? String(pictureUrl).trim() : '';
  const showPhoto = !initialsOnly && url.length > 0 && !imgFailed;

  const commonA11y = {
    'aria-label': ariaLabel,
    role: ariaLabel ? 'img' : undefined,
    'aria-hidden': ariaLabel ? undefined : true,
  };

  if (showPhoto) {
    return (
      <span
        className={`relative block shrink-0 overflow-hidden rounded-full ${sizeClass} ${className}`}
        {...commonA11y}
      >
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      </span>
    );
  }

  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold leading-none text-white ${sizeClass} ${textClass} ${className}`}
      style={{ backgroundColor: bg }}
      {...commonA11y}
    >
      {initials}
    </span>
  );
}
